/**
 * Voxel / lattice neighborhood topologies (DESIGNS-TRACK-Y5).
 * Enemy policy A: opposite neighbor of the friend offset.
 */

import type { Rng } from "../../sim/rng";
import type { TopologyPlugin } from "../types";
import {
  autoGridShape,
  faceOffsets,
  ijkToIndex,
  indexToIjk,
  mooreOffsets,
  type GridShape,
} from "./gridIndex";

function sampleGlobal(
  rng: Rng,
  n: number,
  self: number,
  avoid: number,
  forbidSelf: boolean,
): number {
  if (n <= 1) return 0;
  for (let t = 0; t < 16; t++) {
    const j = rng.int(n);
    if (forbidSelf && j === self) continue;
    if (j === avoid) continue;
    return j;
  }
  let j = (self + 1) % n;
  if (j === avoid) j = (self + 2) % n;
  return j;
}

type Neighbor = { j: number; dx: number; dy: number; dz: number };

function validNeighbors(
  i: number,
  shape: GridShape,
  offsets: [number, number, number][],
): Neighbor[] {
  const [ix, iy, iz] = indexToIjk(i, shape);
  const out: Neighbor[] = [];
  for (const [dx, dy, dz] of offsets) {
    const j = ijkToIndex(ix + dx, iy + dy, iz + dz, shape);
    if (j >= 0 && j !== i) out.push({ j, dx, dy, dz });
  }
  return out;
}

function assignOppositeEnemy(
  i: number,
  f: Neighbor,
  shape: GridShape,
  valid: Neighbor[],
  rng: Rng,
  n: number,
  forbidSelf: boolean,
): number {
  const [ix, iy, iz] = indexToIjk(i, shape);
  const jOpp = ijkToIndex(ix - f.dx, iy - f.dy, iz - f.dz, shape);
  if (jOpp >= 0 && jOpp !== i && jOpp !== f.j) return jOpp;
  const cand = valid.filter((v) => v.j !== f.j);
  if (cand.length) return cand[rng.int(cand.length)]!.j;
  return sampleGlobal(rng, n, i, f.j, forbidSelf);
}

function buildVoxelTopology(
  params: { n: number; dim: number; forbidSelfLinks: boolean },
  rng: Rng,
  offsets: [number, number, number][],
): { friends: Int32Array; enemies: Int32Array } {
  const n = params.n;
  const dim = (params.dim === 3 ? 3 : 2) as 2 | 3;
  const shape = autoGridShape(n, dim);
  const friends = new Int32Array(n);
  const enemies = new Int32Array(n);
  const forbid = params.forbidSelfLinks;

  for (let i = 0; i < n; i++) {
    const valid = validNeighbors(i, shape, offsets);
    if (!valid.length) {
      const fb = n > 1 ? (i + 1) % n : i;
      friends[i] = forbid && n > 1 ? fb : i;
      enemies[i] = n > 1 ? (friends[i]! + 1) % n : friends[i]!;
      if (forbid && enemies[i] === i && n > 1) enemies[i] = (i + 1) % n;
      continue;
    }
    const f = valid[rng.int(valid.length)]!;
    friends[i] = f.j;
    enemies[i] = assignOppositeEnemy(i, f, shape, valid, rng, n, forbid);
  }
  return { friends, enemies };
}

export const voxelFace6Topology: TopologyPlugin = {
  id: "voxel_face6",
  label: "Voxel face-6",
  tier: "advanced",
  description: "Friends on face neighbors (4/6); enemy opposite",
  init(params, rng) {
    const dim = (params.dim === 3 ? 3 : 2) as 2 | 3;
    return buildVoxelTopology(params, rng, faceOffsets(dim));
  },
};

export const voxelMoore26Topology: TopologyPlugin = {
  id: "voxel_moore26",
  label: "Voxel Moore-26",
  tier: "advanced",
  description: "Friends on Moore neighborhood (8/26); enemy opposite",
  init(params, rng) {
    const dim = (params.dim === 3 ? 3 : 2) as 2 | 3;
    return buildVoxelTopology(params, rng, mooreOffsets(dim));
  },
};

/** In-plane friends (±x,±y); enemy prefers ±z (3D). Falls back to face6 in 2D. */
export const voxelLayeredTopology: TopologyPlugin = {
  id: "voxel_layered",
  label: "Voxel layered",
  tier: "advanced",
  description: "Sheet friends in xy; enemies along z (3D)",
  init(params, rng) {
    const dim = (params.dim === 3 ? 3 : 2) as 2 | 3;
    if (dim < 3) {
      return buildVoxelTopology(params, rng, faceOffsets(2));
    }
    const n = params.n;
    const shape = autoGridShape(n, 3);
    const friends = new Int32Array(n);
    const enemies = new Int32Array(n);
    const forbid = params.forbidSelfLinks;
    const plane: [number, number, number][] = [
      [1, 0, 0],
      [-1, 0, 0],
      [0, 1, 0],
      [0, -1, 0],
    ];
    const vert: [number, number, number][] = [
      [0, 0, 1],
      [0, 0, -1],
    ];

    for (let i = 0; i < n; i++) {
      const planeN = validNeighbors(i, shape, plane);
      const vertN = validNeighbors(i, shape, vert);
      if (!planeN.length && !vertN.length) {
        friends[i] = n > 1 ? (i + 1) % n : i;
        enemies[i] = n > 1 ? (friends[i]! + 1) % n : friends[i]!;
        continue;
      }
      const fPool = planeN.length ? planeN : vertN;
      const f = fPool[rng.int(fPool.length)]!;
      friends[i] = f.j;
      if (vertN.length) {
        const e = vertN[rng.int(vertN.length)]!;
        enemies[i] =
          e.j !== f.j
            ? e.j
            : assignOppositeEnemy(i, f, shape, fPool, rng, n, forbid);
      } else {
        enemies[i] = assignOppositeEnemy(i, f, shape, fPool, rng, n, forbid);
      }
    }
    return { friends, enemies };
  },
};
